import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Spin,
  Steps,
  Switch,
  TimePicker,
  Upload,
  notification,
  Modal,
} from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PhoneInput from 'react-phone-input-2';
import Cookies from 'js-cookie';
import isEmpty from 'lodash/isEmpty';
import BreadCrumb from '../breadcrumbs';
import FormTitleHeader from '../Header/FormTitleHeader';
import './CreateJobOrder.scss';
import {
  DRAFT_NAME,
  GST_RATE,
  SERVICE_AREA_BOUNDS,
  SERVICE_AREA_ERROR_MESSAGE,
  TIME_FORMAT,
  TIME_INPUT_FORMAT,
  UNIT_TYPE,
  UNIT_TYPE_DISPLAY,
  UNIT_WEIGHT,
  UNIT_WEIGHT_DISPLAY,
} from '../../constants';
import AddressInput from '../../components/Inputs/AddressInput';
import OptionalLabel from '../Elements/OptionalLabel';
import DefaultMapbox from '../Mapbox/DefaultMapbox';
import { ReactSVG } from 'react-svg';
import { CloseOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { imageMaker } from '../../utils/common';
import { formatClientDeliveryWindowOption } from '../../utils/clientDeliveryWindowLabel';
import * as jobOrdersApi from '../../modules/jobOrders/api';
import { getClients } from '../../modules/client/api';
import Pickup from '../../Icons/pickup';
import Dropoff from '../../Icons/dropoff';
import { doCreateJobOrder, doGetDirections, doGetTypes, doGetTypesV2 } from '../../modules/jobOrders/stores/thunk';
import requestFormData from '../../helper/requestFormData';
import { setMessage } from '../../redux/slice';

function parseHmOnDate(timeStr, deliveryDateDayjs) {
  if (!timeStr || !deliveryDateDayjs?.isValid?.()) return null;
  const parts = String(timeStr).split(':');
  const hh = parseInt(parts[0], 10);
  const mm = parseInt(parts[1] ?? '0', 10);
  if (Number.isNaN(hh)) return null;
  return deliveryDateDayjs
    .hour(hh)
    .minute(Number.isNaN(mm) ? 0 : mm)
    .second(0)
    .millisecond(0);
}

const isWithinServiceArea = (lat, lng) => {
  if (lat == null || lng == null) return false;
  const numLat = Number(lat);
  const numLng = Number(lng);
  return SERVICE_AREA_BOUNDS.some(
    ({ sw, ne }) => numLat >= sw.lat && numLat <= ne.lat && numLng >= sw.lng && numLng <= ne.lng,
  );
};

// Regular (non Special Delivery) orders use the client time options pkup NOW / SOON /
// TOMORROW, exactly like the client apps, so the quoted price matches. Special Delivery
// orders use a free date + delivery window instead.

// Effective start time for NOW & SOON: when the pickup time is (almost) "now", add a
// 10-minute lead time for the driver (mirrors client-web / iOS DeliveryWindowCell).
const getEffectiveStart = (pickupTime, option) => {
  if (!pickupTime) return pickupTime;
  const opt = String(option || '').toUpperCase();
  if (opt !== 'NOW' && opt !== 'SOON') return pickupTime;
  const minPast = dayjs().subtract(2, 'minute');
  const maxFuture = dayjs().add(5, 'minute');
  if (!pickupTime.isBefore(minPast) && pickupTime.isBefore(maxFuture)) {
    return pickupTime.add(10, 'minute');
  }
  return pickupTime;
};

// Delivery window derived from pickup time + client time option (client-web parity):
// NOW => 2 hours, SOON => 4 hours, TOMORROW => 2 hours on the next day.
const getDeliveryWindowTimes = (pickupTime, option) => {
  if (!pickupTime) return { startTime: '', endTime: '' };
  const effective = getEffectiveStart(pickupTime, option);
  const hours = String(option || '').toUpperCase() === 'SOON' ? 4 : 2;
  return {
    startTime: effective.format(TIME_FORMAT),
    endTime: effective.add(hours, 'hour').format(TIME_FORMAT),
  };
};

const getDeliveryDate = (option) => {
  const today = dayjs().startOf('day');
  return String(option || '').toUpperCase() === 'TOMORROW' ? today.add(1, 'day') : today;
};

// Pickup times follow driver availability: 07:30 - 17:00 (client-web parity).
const disabledPickupTime = () => ({
  disabledHours: () => [0, 1, 2, 3, 4, 5, 6, 18, 19, 20, 21, 22, 23],
  disabledMinutes: (hour) => {
    if (hour === 7) return Array.from({ length: 30 }, (_, m) => m);
    if (hour === 17) return Array.from({ length: 59 }, (_, m) => m + 1);
    return [];
  },
});

const steps = [
  {
    icon: <ReactSVG className="circle" src="/icons/location_on.svg" />,
  },
  {
    icon: <ReactSVG className="circle" src="/icons/truck.svg" />,
  },
  {
    icon: <ReactSVG className="circle" src="/icons/assignment.svg" />,
  },
  {
    icon: <ReactSVG className="circle" src="/icons/user.svg" />,
  },
  {
    icon: <ReactSVG className="circle" src="/icons/preview.svg" />,
  },
];

const CreateJobOrderPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromOrderId = searchParams.get('fromOrder');
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [current, setCurrent] = useState(0);
  const [markers, setMarkers] = useState([]);
  const [dataForm, setDataForm] = useState();
  const [fileList, setFileList] = useState([]);
  const [polylines, setPolylines] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [selectedType, setSelectedType] = useState();
  const [isSpecialDelivery, setIsSpecialDelivery] = useState(false);
  const [pickUpLocation, setPickupLocation] = useState();
  const [deliveryLocation, setDeliveryLocation] = useState();
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [fetchingClients, setFetchingClients] = useState(false);
  const lastPrefilledOrderIdRef = useRef(null);
  const draftRestoredRef = useRef(false);
  const [api, contextHolder] = notification.useNotification();
  const isFetching = useSelector((state) => state.jobOrder.isFetching);
  const jobOrderId = useSelector((state) => state.jobOrder.jobOrderId);
  const directions = useSelector((state) => state.jobOrder.directions);
  const types = useSelector((state) => state.jobOrder.types);
  const action = useSelector((state) => state.jobOrder.action);
  const status = useSelector((state) => state.jobOrder.status);
  const breadCrumbs = [{ text: 'JOB ORDERS', url: '/job-orders' }, { text: 'CREATE NEW JOB ORDER' }];

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const pickupTimeWatch = Form.useWatch('pickupTime', form);

  // Regular orders: the delivery window is derived from the pickup time + client time
  // option (client-web parity), so the posted window/date can never disagree with it.
  const [deliverySnapshot, setDeliverySnapshot] = useState({
    pickupTime: null,
    startTime: '',
    endTime: '',
    deliveryDate: null,
  });

  const snapshotDeliveryWindow = (option, pt) => {
    if (!pt) return;
    const opt = String(option || 'TOMORROW').toUpperCase();
    let start = getEffectiveStart(pt, opt);
    // pkup NOW: if the (effective) start is more than 5 minutes in the past, bump
    // another +10 minutes so the driver still has a realistic lead time.
    if (opt === 'NOW') {
      const fiveMinAgo = dayjs().subtract(5, 'minute');
      if (start.isBefore(fiveMinAgo)) {
        start = start.add(10, 'minute');
      }
    }
    const hours = opt === 'SOON' ? 4 : 2;
    const end = start.add(hours, 'hour');
    const deliveryDate = opt === 'TOMORROW' ? dayjs().add(1, 'day').startOf('day') : dayjs().startOf('day');
    setDeliverySnapshot({
      pickupTime: start,
      startTime: start.format(TIME_FORMAT),
      endTime: end.format(TIME_FORMAT),
      deliveryDate,
    });
  };

  // NOW / SOON availability follows the pickup time vs the current time.
  const nowBuffer = dayjs().subtract(40, 'minute');
  let isNowEnabled = false;
  let isSoonEnabled = false;
  if (pickupTimeWatch) {
    const ph = pickupTimeWatch.hour();
    const pm = pickupTimeWatch.minute();
    const nh = nowBuffer.hour();
    const nm = nowBuffer.minute();
    const sameOrLater = ph > nh || (ph === nh && pm >= nm);
    if (sameOrLater) {
      isNowEnabled = (ph > 7 || (ph === 7 && pm >= 30)) && ph < 17;
      isSoonEnabled = (ph > 7 || (ph === 7 && pm >= 30)) && ph <= 14;
    }
  }

  const deliveryWindowDesc = (option) => {
    if (!pickupTimeWatch) return 'Select pickup time to see window';
    const { startTime, endTime } = getDeliveryWindowTimes(pickupTimeWatch, option);
    return String(option).toUpperCase() === 'TOMORROW'
      ? `${startTime} - ${endTime} (next day delivery)`
      : `${startTime} - ${endTime}`;
  };

  // Start/end time of the delivery window for either order kind.
  const getWindowTimes = (values) => {
    if (isSpecialDelivery) {
      const [winStart, winEnd] = values?.deliveryWindow || [];
      return {
        startTime: winStart ? winStart.format(TIME_FORMAT) : '',
        endTime: winEnd ? winEnd.format(TIME_FORMAT) : '',
      };
    }
    return getDeliveryWindowTimes(values?.pickupTime, values?.clientDeliveryWindowOption || 'TOMORROW');
  };

  useEffect(() => {
    dispatch(doGetTypes());
  }, [dispatch]);

  // Active clients a regular order can be assigned to.
  useEffect(() => {
    (async () => {
      setFetchingClients(true);
      const { response, error } = await getClients({ take: 10000, isActive: true });
      if (!error) setClients(response?.users || []);
      setFetchingClients(false);
    })();
  }, []);

  useEffect(() => {
    if (!fromOrderId) {
      lastPrefilledOrderIdRef.current = null;
    }
  }, [fromOrderId]);

  useEffect(() => {
    const { startTime, endTime } = getWindowTimes(dataForm);
    if (startTime && endTime && pickUpLocation?.latLng?.lat && deliveryLocation?.latLng?.lat) {
      dispatch(
        doGetTypesV2({
          origin: { lat: pickUpLocation.latLng.lat, lng: pickUpLocation.latLng.lng },
          destination: { lat: deliveryLocation.latLng.lat, lng: deliveryLocation.latLng.lng },
          startTime,
          endTime,
        }),
      );
    }
  }, [
    isSpecialDelivery,
    dataForm?.deliveryWindow,
    dataForm?.pickupTime,
    dataForm?.clientDeliveryWindowOption,
    pickUpLocation?.latLng?.lat,
    deliveryLocation?.latLng?.lat,
  ]);

  useEffect(() => {
    // Restore the draft (or initialize a fresh form) only once per mount. This effect
    // must not re-run when `types` is refreshed by a v2 quote request, otherwise the
    // restore keeps resetting the wizard step and form values (infinite loop).
    if (draftRestoredRef.current) return;
    if (!types?.length) return;
    if (fromOrderId) return;
    draftRestoredRef.current = true;

    const dataCookie = Cookies.get(DRAFT_NAME) ? JSON.parse(Cookies.get(DRAFT_NAME)) : undefined;
    if (!isEmpty(dataCookie)) {
      const pickupTime = dataCookie?.data?.pickupTime ? dayjs(dataCookie?.data?.pickupTime) : undefined;
      const items = dataCookie?.data?.items || [{}];
      const deliveryDate = dataCookie?.data?.deliveryDate ? dayjs(dataCookie?.data?.deliveryDate) : undefined;
      const rawWindow = dataCookie?.data?.deliveryWindow;
      const deliveryWindow =
        Array.isArray(rawWindow) && rawWindow[0] && rawWindow[1]
          ? [dayjs(rawWindow[0]), dayjs(rawWindow[1])]
          : undefined;
      const clientDeliveryWindowOption = dataCookie?.data?.clientDeliveryWindowOption || 'TOMORROW';

      setFileList(dataCookie.fileList || []);
      setDataForm({
        ...dataCookie.data,
        pickupTime,
        items,
        deliveryDate,
        deliveryWindow,
        clientDeliveryWindowOption,
      });
      form.setFieldsValue({
        ...dataCookie.data,
        pickupTime,
        items,
        deliveryDate,
        deliveryWindow,
        clientDeliveryWindowOption,
      });
      setPickupLocation(dataCookie.pickUpLocation);
      setDeliveryLocation(dataCookie.deliveryLocation);
      setCurrent(dataCookie.current);
      setSelectedType(dataCookie.selectedType || types?.[0]?.type);
      setIsSpecialDelivery(!!dataCookie.isSpecialDelivery);
      if (pickupTime && !dataCookie.isSpecialDelivery) {
        snapshotDeliveryWindow(clientDeliveryWindowOption, pickupTime);
      }
    } else {
      form.setFieldsValue({
        items: [
          {
            name: '',
            unit: UNIT_TYPE.cm,
            unitWeight: UNIT_WEIGHT.kg,
            length: '',
            width: '',
            height: '',
            weight: '',
            quantity: 1,
          },
        ],
      });
      setSelectedType(types?.[0]?.type);
    }
  }, [types, fromOrderId]);

  useEffect(() => {
    if (!fromOrderId || !types?.length) return;
    if (lastPrefilledOrderIdRef.current === fromOrderId) return;
    let cancelled = false;

    (async () => {
      setPrefillLoading(true);
      const { response: job, error } = await jobOrdersApi.getJobOrder(fromOrderId);
      if (cancelled) return;
      setPrefillLoading(false);

      if (error || !job?.pickupAddress) {
        api.error({
          message: 'Could not load order',
          description: (error && error.message) || 'Check the order id and try again.',
        });
        return;
      }

      const pickupTime = parseHmOnDate(job.pickupTime, dayjs());

      const nextPickUpLocation = {
        address: job.pickupAddress,
        latLng: {
          lat: Number(job.pickupAddressLat),
          lng: Number(job.pickupAddressLng),
        },
      };
      const nextDeliveryLocation = {
        address: job.deliveryAddress,
        latLng: {
          lat: Number(job.deliveryAddressLat),
          lng: Number(job.deliveryAddressLng),
        },
      };

      const rawItems = job.packages || [];
      const items =
        rawItems.length > 0
          ? rawItems.map((p) => ({
              name: p.name ?? '',
              length: p.length ?? '',
              width: p.width ?? '',
              height: p.height ?? '',
              weight: p.weight ?? '',
              quantity: p.quantity ?? 1,
              unit: p.unit || UNIT_TYPE.cm,
              unitWeight: p.unitWeight || UNIT_WEIGHT.kg,
            }))
          : [
              {
                name: '',
                unit: UNIT_TYPE.cm,
                unitWeight: UNIT_WEIGHT.kg,
                length: '',
                width: '',
                height: '',
                weight: '',
                quantity: 1,
              },
            ];

      const nextFileList = (job.files || []).map((f) => ({
        uid: String(f.id),
        id: f.id,
        name: f.key || `file-${f.id}`,
        url: f.url,
        status: 'done',
        key: f.key,
      }));

      const deliveryDate = job.deliveryDate ? dayjs(job.deliveryDate) : null;
      const windowStart = parseHmOnDate(job.deliveryStartTime, deliveryDate || dayjs());
      const windowEnd = parseHmOnDate(job.deliveryEndTime, deliveryDate || dayjs());
      const deliveryWindow = windowStart && windowEnd ? [windowStart, windowEnd] : undefined;
      const opt = job.clientDeliveryWindowOption;
      const clientDeliveryWindowOption =
        opt && ['NOW', 'SOON', 'TOMORROW'].includes(String(opt).toUpperCase()) ? String(opt).toUpperCase() : 'TOMORROW';

      const formValues = {
        addressPickup: nextPickUpLocation,
        addressDelivery: nextDeliveryLocation,
        pickupTime,
        items,
        instruction: job.instruction || '',
        contactName: job.contactName || '',
        contactPhone: job.contactPhone || '',
        deliveryInstruction: job.deliveryInstruction || '',
        deliveryContactName: job.deliveryContactName || '',
        deliveryContactPhone: job.deliveryContactPhone || '',
        specialDeliveryDetails: job.specialDeliveryDetails || '',
        clientId: job.user?.role === 'CLIENT' && job.user?.isActive ? job.user.id : undefined,
        deliveryDate,
        deliveryWindow,
        clientDeliveryWindowOption,
      };

      form.setFieldsValue(formValues);
      setDataForm(formValues);
      setPickupLocation(nextPickUpLocation);
      setDeliveryLocation(nextDeliveryLocation);
      setSelectedType(job.type || types[0]?.type);
      setIsSpecialDelivery(job.isSpecialDelivery === true);
      setFileList(nextFileList);
      setCurrent(0);
      setDeliverySnapshot({
        pickupTime: pickupTime || null,
        startTime: job.deliveryStartTime || '',
        endTime: job.deliveryEndTime || '',
        deliveryDate: job.deliveryDate ? dayjs(job.deliveryDate) : null,
      });

      lastPrefilledOrderIdRef.current = fromOrderId;

      api.success({
        message: 'Order loaded',
        description: 'Fields are prefilled from this order. Submit to create a new order.',
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [fromOrderId, types]);

  useEffect(() => {
    if (action === 'create' && status === 'succeeded') {
      Cookies.set(DRAFT_NAME, JSON.stringify({}));
      // Navigate to payment page for the newly created job order.
      // Special Deliveries are charged the same way: base = Job Price entered
      // by admin, GST added on top by Stripe Tax when the card is charged.
      navigate(`/job-orders/payment/${jobOrderId}`);
    }
  }, [action, status, navigate, jobOrderId]);

  useEffect(() => {
    if (pickUpLocation?.latLng?.lat && deliveryLocation?.latLng?.lat) {
      dispatch(
        doGetDirections({
          origin: { lat: pickUpLocation.latLng.lat, lng: pickUpLocation.latLng.lng },
          destination: { lat: deliveryLocation.latLng.lat, lng: deliveryLocation.latLng.lng },
        }),
      );
    }
  }, [pickUpLocation, deliveryLocation]);

  useEffect(() => {
    if (directions?.polyline) {
      setPolylines([{ polyline: directions.polyline, strokeColor: '#063A3B', strokeWeight: 6, strokeOpacity: 0.8 }]);
    } else if (polylines.length > 0) {
      setPolylines([]);
    }
  }, [directions, pickUpLocation, deliveryLocation]);

  const handleUploadFile = async (options) => {
    const { onSuccess, onError, file } = options;
    const fmData = new FormData();
    const newFileList = [...fileList, { ...file, status: 'uploading' }];
    setFileList(newFileList);
    fmData.append('file', file);

    try {
      const { response: fileRs, error } = await requestFormData('/common/shared-upload', {
        method: 'POST',
        data: fmData,
      });
      if (fileRs) {
        const fileIndex = newFileList.findIndex((f) => f.uid === (file.uid || file.id));
        const isPdf = fileRs.key?.toLowerCase().endsWith('.pdf') || file.type === 'application/pdf';
        newFileList.splice(fileIndex, 1, {
          uid: fileRs.id,
          ...fileRs,
          name: fileRs.key,
          status: 'done',
          thumbUrl: isPdf
            ? '/icons/pdf.svg'
            : file instanceof File
              ? URL.createObjectURL(file)
              : file.thumbUrl || file.url,
        });
        setFileList(newFileList);
        onSuccess('ok');
      } else {
        console.log(error);
        const fileIndex = fileList.findIndex((f) => f.uid === file.uid);
        newFileList.splice(fileIndex, 1, {
          ...file,
          name: file.name,
          status: 'error',
        });
        setFileList(newFileList);
        onError();
      }
    } catch (e) {
      console.log(e);
    }
  };

  const removeFile = async (file) => {
    const newFileList = [...fileList];
    const fileIndex = fileList.findIndex((f) => f.uid === file.uid);
    newFileList.splice(fileIndex, 1);
    setFileList(newFileList);
  };

  const handlePreviewFile = async (file) => {
    let preview = file.url || file.preview;
    if (!preview && file.originFileObj) {
      preview = URL.createObjectURL(file.originFileObj);
    }
    const fileUrl = preview || file?.thumbUrl || file?.response?.url;
    if (fileUrl) {
      const isPdf =
        file.name?.toLowerCase().endsWith('.pdf') ||
        fileUrl?.toLowerCase().includes('.pdf') ||
        file.response?.key?.toLowerCase().endsWith('.pdf');

      if (isPdf) {
        window.open(fileUrl, '_blank', 'noopener,noreferrer');
      } else {
        setPreviewImage(fileUrl);
        setPreviewOpen(true);
        setPreviewTitle(file.name || (file.url && file.url.substring(file.url.lastIndexOf('/') + 1)) || 'Preview');
      }
    }
  };

  const renderUploadItem = (originNode, file, _fileItems, actions) => {
    if (!file) return originNode;
    return (
      <div className="custom-upload-item-wrapper">
        {originNode}
        {actions?.remove && (
          <button
            type="button"
            className="custom-upload-remove-btn"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              actions.remove();
            }}
          >
            <CloseOutlined />
          </button>
        )}
      </div>
    );
  };

  const continueStep = () => {
    setCurrent(current + 1);
  };

  const clickConfirm = () => {
    form.submit();
  };

  const clickBack = () => {
    if (current > 0) {
      setCurrent(current - 1);
    }
  };

  const saveAsDraft = () => {
    Cookies.set(
      DRAFT_NAME,
      JSON.stringify({
        current,
        // Snapshot the form store (not the local `dataForm` state) so custom inputs
        // such as addresses keep their full value object (address + latLng).
        data: form.getFieldsValue(true),
        fileList: fileList,
        pickUpLocation,
        deliveryLocation,
        selectedType,
        isSpecialDelivery,
      }),
    );
    dispatch(setMessage('Draft saved successfully'));
  };

  const normFile = (e) => {
    if (Array.isArray(e?.fileList)) {
      return e?.fileList;
    }
    return [];
  };

  useEffect(() => {
    const newMarkers = [];
    if (pickUpLocation?.latLng?.lat && pickUpLocation?.latLng?.lng) {
      newMarkers.push({
        id: 1,
        content: <img src={imageMaker(Pickup())} style={{ maxWidth: 40 }} />,
        latitude: Number(pickUpLocation?.latLng?.lat),
        longitude: Number(pickUpLocation?.latLng?.lng),
        offsetX: 0,
        offsetY: 0,
        className: 'order-marker',
      });
    }
    if (deliveryLocation?.latLng?.lat && deliveryLocation?.latLng?.lng) {
      newMarkers.push({
        id: 2,
        content: <img src={imageMaker(Dropoff())} style={{ maxWidth: 40 }} />,
        latitude: Number(deliveryLocation?.latLng?.lat),
        longitude: Number(deliveryLocation?.latLng?.lng),
        offsetX: 0,
        offsetY: 0,
        className: 'order-marker',
      });
    }
    setMarkers(newMarkers);
  }, [pickUpLocation, deliveryLocation]);

  const parseItems = (items) => {
    return (
      items?.map((item) => {
        return {
          ...item,
          weight: item.weight ? Number(item.weight) : null,
          length: item.length ? Number(item.length) : null,
          width: item.width ? Number(item.width) : null,
          height: item.height ? Number(item.height) : null,
          quantity: item.quantity ? Number(item.quantity) : null,
        };
      }) || []
    );
  };

  const filterOption = (input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
  const clientLabel = (client) => `${client.name || 'N/A'} (${client.email})`;

  // Regular orders can belong to a client; otherwise (and always for Special
  // Deliveries) the order belongs to admin.
  const ownerClient = !isSpecialDelivery ? clients.find((c) => c.id === dataForm?.clientId) : undefined;

  const handleOnFinish = async () => {
    if (current === 4) {
      let deliveryDate;
      let pickupTime;
      let deliveryStartTime;
      let deliveryEndTime;
      let clientDeliveryWindowOption;
      if (isSpecialDelivery) {
        const [winStart, winEnd] = dataForm.deliveryWindow || [];
        deliveryDate = dataForm.deliveryDate.startOf('day');
        pickupTime = dataForm.pickupTime;
        deliveryStartTime = winStart.format(TIME_FORMAT);
        deliveryEndTime = winEnd.format(TIME_FORMAT);
      } else {
        clientDeliveryWindowOption = String(dataForm.clientDeliveryWindowOption || 'TOMORROW').toUpperCase();
        const derived = getDeliveryWindowTimes(dataForm.pickupTime, clientDeliveryWindowOption);
        deliveryDate = deliverySnapshot.deliveryDate || getDeliveryDate(clientDeliveryWindowOption);
        pickupTime = deliverySnapshot.pickupTime || dataForm.pickupTime;
        deliveryStartTime = deliverySnapshot.startTime || derived.startTime;
        deliveryEndTime = deliverySnapshot.endTime || derived.endTime;
      }
      const data = {
        deliveryDate: deliveryDate.toISOString(),
        pickupTime: pickupTime?.format(TIME_FORMAT),
        pickupAddress: pickUpLocation.address,
        pickupAddressLat: String(pickUpLocation.latLng.lat),
        pickupAddressLng: String(pickUpLocation.latLng.lng),
        deliveryAddress: deliveryLocation.address,
        deliveryAddressLat: String(deliveryLocation.latLng.lat),
        specialDeliveryDetails: isSpecialDelivery ? dataForm.specialDeliveryDetails : undefined,
        instruction: dataForm.instruction,
        deliveryAddressLng: String(deliveryLocation.latLng.lng),
        fileIds: fileList.filter((file) => file.id).map((file) => file.id),
        contactPhone: dataForm.contactPhone,
        contactName: dataForm.contactName,
        type: selectedType,
        isSpecialDelivery,
        clientId: ownerClient?.id,
        jobPrice: isSpecialDelivery ? Number(dataForm.jobPrice) : undefined,
        driverRate: isSpecialDelivery ? Number(dataForm.driverRate) : undefined,
        deliveryInstruction: dataForm.deliveryInstruction,
        deliveryContactPhone: dataForm.deliveryContactPhone,
        deliveryContactName: dataForm.deliveryContactName,
        packages: parseItems(dataForm.items),
        deliveryStartTime,
        deliveryEndTime,
        clientDeliveryWindowOption,
        timezone,
      };
      dispatch(doCreateJobOrder(data));
    } else {
      continueStep();
    }
  };

  const onValuesChange = (changedValues, allValues) => {
    setDataForm({ ...dataForm, ...allValues });
    // Regular orders: keep the derived delivery window in sync when the pickup time
    // or the client time option changes.
    if ('pickupTime' in changedValues || 'clientDeliveryWindowOption' in changedValues) {
      snapshotDeliveryWindow(allValues.clientDeliveryWindowOption || 'TOMORROW', allValues.pickupTime);
    }
  };

  const handleRemoveItems = (items, itemKey, remove) => {
    if (items.length === 1) {
      api.error({
        message: `Please have at least 1 item.`,
      });
      return;
    } else {
      remove(itemKey);
    }
  };

  const jobPriceValue = Number(dataForm?.jobPrice) || 0;
  const driverRateValue = Number(dataForm?.driverRate) || 0;
  // Special Delivery: the client is charged the Job Price with GST added on
  // top (Stripe Tax recalculates the exact amount when the card is charged).
  const clientChargedAmount = jobPriceValue > 0 ? jobPriceValue * (1 + GST_RATE) : null;
  const derivedWindowTimes = getWindowTimes(dataForm);
  const reviewWindowStart = isSpecialDelivery
    ? derivedWindowTimes.startTime
    : deliverySnapshot.startTime || derivedWindowTimes.startTime;
  const reviewWindowEnd = isSpecialDelivery
    ? derivedWindowTimes.endTime
    : deliverySnapshot.endTime || derivedWindowTimes.endTime;
  const reviewDeliveryDate = isSpecialDelivery
    ? dataForm?.deliveryDate
    : deliverySnapshot.deliveryDate || getDeliveryDate(dataForm?.clientDeliveryWindowOption || 'TOMORROW');

  return (
    <Spin spinning={isFetching || prefillLoading}>
      {contextHolder}
      <div className="Content CreateJobOrderPage">
        <BreadCrumb breadCrumbs={breadCrumbs} />
        <div className="FormContent">
          <FormTitleHeader title="Create New Order" />
          <Divider />
          <Steps className="steps-track" current={current} items={steps} labelPlacement="vertical" />
          <Form
            form={form}
            scrollToFirstError
            layout="vertical"
            requiredMark={false}
            colon={false}
            onFinish={handleOnFinish}
            onValuesChange={onValuesChange}
          >
            <div className="step-content">
              {current === 0 && (
                <>
                  <div className="header-step">
                    <div className="title">
                      <h5>Create New pkup</h5>
                    </div>
                  </div>
                  <Row gutter={[20, 24]}>
                    <Col span={24}>
                      <Form.Item label="Special Delivery">
                        <Switch checked={isSpecialDelivery} onChange={(checked) => setIsSpecialDelivery(checked)} />
                      </Form.Item>
                    </Col>
                    {!isSpecialDelivery && (
                      <Col span={24}>
                        <Form.Item label={<OptionalLabel title="Client" />} name="clientId">
                          <Select
                            size="large"
                            // Global styles make large controls 48px tall but antd keeps the select itself at
                            // 40px; match it so the arrow is centered and the box does not overflow its item.
                            style={{ width: '100%', height: 48 }}
                            showSearch
                            allowClear
                            placeholder="Admin"
                            filterOption={filterOption}
                            loading={fetchingClients}
                            options={clients.map((c) => ({ label: clientLabel(c), value: c.id }))}
                          />
                        </Form.Item>
                      </Col>
                    )}
                    {isSpecialDelivery && (
                      <>
                        <Col span={12}>
                          <Form.Item
                            label="Job Price (before GST)"
                            name="jobPrice"
                            rules={[{ required: true, message: 'Required' }]}
                            extra={
                              clientChargedAmount != null ? (
                                <>
                                  Client will be charged <b>${clientChargedAmount.toFixed(2)}</b> (incl.{' '}
                                  {GST_RATE * 100}% GST)
                                </>
                              ) : null
                            }
                          >
                            <Input placeholder="Job Price (before GST)" size="large" type="number" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label="Driver Rate (before GST)"
                            name="driverRate"
                            dependencies={['jobPrice']}
                            rules={[
                              { required: true, message: 'Required' },
                              ({ getFieldValue }) => ({
                                validator(_, value) {
                                  const jobPrice = Number(getFieldValue('jobPrice'));
                                  const driverRate = Number(value);
                                  if (value && jobPrice > 0 && driverRate > jobPrice) {
                                    return Promise.reject(new Error('Driver Rate must not exceed Job Price'));
                                  }
                                  return Promise.resolve();
                                },
                              }),
                            ]}
                            extra={
                              driverRateValue > 0 ? (
                                <>
                                  Driver receives <b>${(driverRateValue * (1 + GST_RATE)).toFixed(2)}</b> (incl.{' '}
                                  {GST_RATE * 100}% GST)
                                </>
                              ) : null
                            }
                          >
                            <Input placeholder="Driver Rate" size="large" type="number" />
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item
                            label="Special Delivery Details"
                            name="specialDeliveryDetails"
                            extra="The driver must read these details before accepting this job."
                          >
                            <Input.TextArea placeholder="Describe this special delivery" size="large" rows={4} />
                          </Form.Item>
                        </Col>
                      </>
                    )}
                    <Col span={24}>
                      <Form.Item
                        label="Pickup Location"
                        name="addressPickup"
                        rules={[
                          {
                            validator: (_, value) => {
                              if (!value) {
                                return Promise.reject(new Error('Required'));
                              }
                              if (!value?.latLng?.lat || !value?.latLng?.lng) {
                                return isEmpty(value?.address)
                                  ? Promise.reject(new Error('Required'))
                                  : Promise.reject(new Error('Invalid'));
                              }
                              if (!isSpecialDelivery && !isWithinServiceArea(value.latLng.lat, value.latLng.lng)) {
                                return Promise.reject(new Error(SERVICE_AREA_ERROR_MESSAGE));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <AddressInput
                          size="large"
                          placeholder="Pickup Location"
                          onSelect={(value) => {
                            if (!value?.latLng?.lat || !value?.latLng?.lng) {
                              form.setFieldsValue({ addressPickup: value });
                              setPickupLocation(value);
                              return;
                            }
                            if (!isSpecialDelivery && !isWithinServiceArea(value.latLng.lat, value.latLng.lng)) {
                              api.error({
                                message: 'Service Area Restricted',
                                description: SERVICE_AREA_ERROR_MESSAGE,
                              });
                              return;
                            }
                            form.setFieldsValue({ addressPickup: value });
                            setPickupLocation(value);
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        label="Pickup Time"
                        name="pickupTime"
                        rules={[{ required: true, message: 'Required' }]}
                      >
                        <TimePicker
                          format={TIME_INPUT_FORMAT}
                          placeholder="Pickup Time"
                          size="large"
                          minuteStep={1}
                          disabledTime={disabledPickupTime}
                        />
                      </Form.Item>
                    </Col>
                    {isSpecialDelivery ? (
                      <>
                        <Col span={24}>
                          <Form.Item label="Date" name="deliveryDate" rules={[{ required: true, message: 'Required' }]}>
                            <DatePicker
                              format="DD/MM/YYYY"
                              placeholder="Select date"
                              size="large"
                              style={{ width: '100%' }}
                              disabledDate={(d) => d && d.isBefore(dayjs().startOf('day'))}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item
                            label="Delivery Window"
                            name="deliveryWindow"
                            rules={[
                              {
                                validator: (_, value) => {
                                  if (!value || !value[0] || !value[1]) {
                                    return Promise.reject(new Error('Required'));
                                  }
                                  if (!value[1].isAfter(value[0])) {
                                    return Promise.reject(new Error('End time must be after start time'));
                                  }
                                  return Promise.resolve();
                                },
                              },
                            ]}
                          >
                            <TimePicker.RangePicker
                              format={TIME_INPUT_FORMAT}
                              placeholder={['Start time', 'End time']}
                              size="large"
                              minuteStep={5}
                              style={{ width: '100%' }}
                            />
                          </Form.Item>
                        </Col>
                      </>
                    ) : (
                      <Col span={24}>
                        <Form.Item
                          label="Delivery Window"
                          name="clientDeliveryWindowOption"
                          initialValue="TOMORROW"
                          rules={[{ required: true, message: 'Required' }]}
                        >
                          <Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <Radio value="NOW" disabled={!isNowEnabled}>
                              <span>pkup NOW (2 hours)</span>
                              <div style={{ fontSize: 12, color: '#888', lineHeight: '18px' }}>
                                {deliveryWindowDesc('NOW')}
                              </div>
                            </Radio>
                            <Radio value="SOON" disabled={!isSoonEnabled}>
                              <span>pkup SOON (4 hours)</span>
                              <div style={{ fontSize: 12, color: '#888', lineHeight: '18px' }}>
                                {deliveryWindowDesc('SOON')}
                              </div>
                            </Radio>
                            <Radio value="TOMORROW">
                              <span>pkup TOMORROW</span>
                              <div style={{ fontSize: 12, color: '#888', lineHeight: '18px' }}>
                                {deliveryWindowDesc('TOMORROW')}
                              </div>
                            </Radio>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                    )}
                    <Col span={24}>
                      <Form.Item
                        label="Drop off Location"
                        name="addressDelivery"
                        rules={[
                          {
                            validator: (_, value) => {
                              if (!value) {
                                return Promise.reject(new Error('Required'));
                              }
                              if (!value?.latLng?.lat || !value?.latLng?.lng) {
                                return isEmpty(value?.address)
                                  ? Promise.reject(new Error('Required'))
                                  : Promise.reject(new Error('Invalid'));
                              }
                              if (!isSpecialDelivery && !isWithinServiceArea(value.latLng.lat, value.latLng.lng)) {
                                return Promise.reject(new Error(SERVICE_AREA_ERROR_MESSAGE));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <AddressInput
                          size="large"
                          placeholder="Delivery Location"
                          onSelect={(value) => {
                            if (!value?.latLng?.lat || !value?.latLng?.lng) {
                              form.setFieldsValue({ addressDelivery: value });
                              setDeliveryLocation(value);
                              return;
                            }
                            if (!isSpecialDelivery && !isWithinServiceArea(value.latLng.lat, value.latLng.lng)) {
                              api.error({
                                message: 'Service Area Restricted',
                                description: SERVICE_AREA_ERROR_MESSAGE,
                              });
                              return;
                            }
                            form.setFieldsValue({ addressDelivery: value });
                            setDeliveryLocation(value);
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Divider />
                    <Col span={24}>
                      <div className="wrapper-map">
                        <DefaultMapbox markers={markers} className="mapbox-order-detail" polylines={polylines} />
                      </div>
                    </Col>
                  </Row>
                </>
              )}
              {current === 1 && (
                <>
                  <div className="header-step">
                    <div className="title">
                      <h5>Select P&apos;KUP Type</h5>
                    </div>
                    <div className="content">
                      <p>
                        Please make sure that your item(s) fit within the vehicle&apos;s maximum capacity limits.
                        <p>
                          Driver is not expected to lift items greater than 25 KG. Any pickup exceeding this weight
                          limit will require independent arrangements made by customer.
                        </p>
                      </p>
                    </div>
                  </div>
                  <div className="types">
                    <Radio.Group value={selectedType}>
                      <Row gutter={[20, 24]}>
                        {types.map((typeValue) => (
                          <Col span={12} key={typeValue.type}>
                            <div
                              className={`type ${typeValue.type === selectedType ? ' active' : ''}`}
                              onClick={() => setSelectedType(typeValue.type)}
                            >
                              <div className="type-header">
                                <Radio value={typeValue.type} />
                                <p>{typeValue.displayName}</p>
                              </div>
                              <div className="type-info">
                                <div className="image">
                                  <img src={typeValue.imageSrc} alt={typeValue.displayName} />
                                </div>
                                <div className="type-description">
                                  <p>{typeValue.description}</p>
                                  <p>(MAX {typeValue.payloadMax} KG)</p>
                                </div>
                                <div className="type-price">
                                  <b>{typeValue.price}</b>
                                  <span>Estimated price</span>
                                </div>
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </Radio.Group>
                  </div>
                  <div className="note">
                    <p>
                      Depending on availability, the substitution of your preferred vehicle may occur to ensure your
                      package is delivered as quickly and efficiently as possible.
                    </p>
                  </div>
                </>
              )}
              {current === 2 && (
                <>
                  <div className="header-step">
                    <div className="title">
                      <h5>Order Details</h5>
                    </div>
                  </div>
                  <Row gutter={[20, 24]}>
                    <Form.List name="items">
                      {(fields, { add, remove }) => {
                        return (
                          <>
                            {fields.map((field) => (
                              <React.Fragment key={field.key}>
                                <Col span={23}>
                                  <Form.Item
                                    label="Item Name"
                                    name={[field.name, 'name']}
                                    rules={[{ required: true, message: 'Required' }]}
                                  >
                                    <Input placeholder="Item Name" size="large" />
                                  </Form.Item>
                                </Col>
                                <Col span={1}>
                                  <div className="type-delete">
                                    <ReactSVG
                                      className="icon"
                                      src="/icons/delete_forever.svg"
                                      onClick={() => handleRemoveItems(fields, field.key, remove)}
                                    />
                                  </div>
                                </Col>
                                <Col span={6}>
                                  <Form.Item label="Unit" name={[field.name, 'unit']}>
                                    <Select>
                                      {Object.keys(UNIT_TYPE).map((key) => (
                                        <Select.Option key={key} value={UNIT_TYPE[key]}>
                                          {key}
                                        </Select.Option>
                                      ))}
                                    </Select>
                                  </Form.Item>
                                </Col>
                                <Col span={6}>
                                  <Form.Item label="Length" name={[field.name, 'length']}>
                                    <Input placeholder="Length" size="large" type="number" />
                                  </Form.Item>
                                </Col>
                                <Col span={6}>
                                  <Form.Item label="Width" name={[field.name, 'width']}>
                                    <Input placeholder="Width" size="large" type="number" />
                                  </Form.Item>
                                </Col>
                                <Col span={6}>
                                  <Form.Item label="Height" name={[field.name, 'height']}>
                                    <Input placeholder="Height" size="large" type="number" />
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item label="Unit" name={[field.name, 'unitWeight']}>
                                    <Select>
                                      {Object.keys(UNIT_WEIGHT).map((key) => (
                                        <Select.Option key={key} value={UNIT_WEIGHT[key]}>
                                          {key}
                                        </Select.Option>
                                      ))}
                                    </Select>
                                  </Form.Item>
                                </Col>
                                <Col span={10}>
                                  <Form.Item label="Weight" name={[field.name, 'weight']}>
                                    <Input placeholder="Weight" size="large" type="number" />
                                  </Form.Item>
                                </Col>
                                <Col span={10}>
                                  <Form.Item label="Quantity" name={[field.name, 'quantity']}>
                                    <Input placeholder="Quantity" size="large" type="number" />
                                  </Form.Item>
                                </Col>
                              </React.Fragment>
                            ))}
                            <Col span={24}>
                              <Button
                                type="link"
                                ghost
                                className="add-item"
                                onClick={() => {
                                  add({
                                    name: '',
                                    unit: UNIT_TYPE.ft,
                                    unitWeight: UNIT_WEIGHT.lbs,
                                    length: '',
                                    width: '',
                                    height: '',
                                    weight: '',
                                    quantity: 1,
                                  });
                                }}
                              >
                                Add Item
                                <ReactSVG className="icon" src="/icons/add_circle_outline.svg" />
                              </Button>
                            </Col>
                          </>
                        );
                      }}
                    </Form.List>
                    <Divider />
                    <Col span={24}>
                      <Form.Item
                        label="Photo/Document of receipt, order number, item(s)"
                        name="documents"
                        getValueFromEvent={normFile}
                      >
                        <Upload.Dragger
                          multiple={false}
                          fileList={fileList}
                          showUploadList={{
                            showPreviewIcon: true,
                            showDownloadIcon: false,
                            showRemoveIcon: false,
                          }}
                          itemRender={renderUploadItem}
                          onPreview={handlePreviewFile}
                          customRequest={handleUploadFile}
                          onRemove={removeFile}
                        >
                          <p className="upload-text">Drag and drop to upload or</p>
                          <p className="upload-drag-icon">
                            Browse
                            <UploadOutlined />
                          </p>
                        </Upload.Dragger>
                      </Form.Item>
                    </Col>
                    <Divider />
                    <Col span={24}>
                      <Form.Item label="Pickup Instruction" name="instruction">
                        <Input.TextArea placeholder="Pickup Instruction" size="large" rows={8} />
                      </Form.Item>
                    </Col>
                    <Divider />
                    <Col span={12}>
                      <Form.Item
                        label="Contact Details for Pickup"
                        name="contactName"
                        rules={[{ required: true, message: 'Required' }]}
                      >
                        <Input placeholder="Contact Details for Pickup" size="large" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label=" " name="contactPhone" rules={[{ required: true, message: 'Required' }]}>
                        <PhoneInput
                          country={'us'}
                          prefix="+"
                          // specialLabel=""
                          countryCodeEditable
                          enableSearch
                          disableSearchIcon
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}
              {current === 3 && (
                <>
                  <div className="header-step">
                    <div className="title">
                      <h5>Drop-off Details</h5>
                    </div>
                  </div>
                  <Row gutter={[20, 24]}>
                    <Col span={12}>
                      <Form.Item
                        label="Contact Details for Drop-off"
                        name="deliveryContactName"
                        rules={[{ required: true, message: 'Required' }]}
                      >
                        <Input placeholder="Contact Details for Drop-off" size="large" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label=" "
                        name="deliveryContactPhone"
                        rules={[{ required: true, message: 'Required' }]}
                      >
                        <PhoneInput
                          country={'us'}
                          prefix="+"
                          specialLabel=""
                          countryCodeEditable
                          enableSearch
                          disableSearchIcon
                        />
                      </Form.Item>
                    </Col>
                    <Divider />
                    <Col span={24}>
                      <Form.Item label="Drop-off Instruction" name="deliveryInstruction">
                        <Input.TextArea placeholder="Drop-off Instruction" size="large" rows={8} />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}
              {current === 4 && (
                <>
                  <div className="header-step">
                    <div className="title">
                      <h5>Review</h5>
                    </div>
                  </div>
                  <div className="review">
                    <div className="review-content">
                      <label>Client</label>
                      <p>{ownerClient ? clientLabel(ownerClient) : 'Admin'}</p>
                    </div>
                    <div className="review-content">
                      <label>Pickup Location</label>
                      <p>{dataForm.addressPickup?.address}</p>
                    </div>
                    <div className="review-content">
                      <label>Pickup Time</label>
                      <p>{dataForm.pickupTime ? dataForm.pickupTime.format(TIME_FORMAT) : 'N/A'}</p>
                    </div>
                    <div className="review-content">
                      <label>Date</label>
                      <p>{reviewDeliveryDate ? reviewDeliveryDate.format('DD/MM/YYYY') : 'N/A'}</p>
                    </div>
                    <div className="review-content">
                      <label>Delivery Window</label>
                      <p>
                        {reviewWindowStart && reviewWindowEnd ? `${reviewWindowStart} - ${reviewWindowEnd}` : 'N/A'}
                        {!isSpecialDelivery &&
                        String(dataForm.clientDeliveryWindowOption || '').toUpperCase() === 'TOMORROW'
                          ? ' (next day delivery)'
                          : ''}
                      </p>
                    </div>
                    {!isSpecialDelivery && (
                      <div className="review-content">
                        <label>Client time option</label>
                        <p>{formatClientDeliveryWindowOption(dataForm.clientDeliveryWindowOption)}</p>
                      </div>
                    )}
                    {isSpecialDelivery && (
                      <div className="review-content">
                        <label>Special Delivery Details</label>
                        <p>{dataForm.specialDeliveryDetails || 'N/A'}</p>
                      </div>
                    )}
                    <div className="review-content">
                      <label>Drop off Locationn</label>
                      <p>{dataForm.addressDelivery?.address}</p>
                    </div>
                    <Divider />
                    <div className="review-content">
                      <label>Vehicle Type</label>
                      <p>{types.find((t) => t.type === selectedType)?.displayName}</p>
                    </div>
                    <Divider />
                    <h6 className="sub-title">Pickup From Location</h6>
                    <div className="review-content">
                      <label>Item(s) for pickup</label>
                      <div className="review-items">
                        {dataForm.items.map((item, i) => (
                          <div className="review-item" key={item.name + `${i}`}>
                            <div className="review-item-left">.</div>
                            <div className="review-item-right">
                              <p>{item.name}</p>
                              <p>
                                {item.length ? `${item.length} ${UNIT_TYPE_DISPLAY[item.unit]}` : 'N/A'} x{' '}
                                {item.width ? `${item.width} ${UNIT_TYPE_DISPLAY[item.unit]}` : 'N/A'} x{' '}
                                {item.height ? `${item.height} ${UNIT_TYPE_DISPLAY[item.unit]}` : 'N/A'}{' '}
                                {item.weight || 'N/A'} {UNIT_WEIGHT_DISPLAY[item.unitWeight]} | Qty:{' '}
                                {item.quantity || 'N/A'}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="review-content">
                      <label>Instruction Note for Pickup</label>
                      <p>{dataForm.instruction || 'N/A'}</p>
                    </div>
                    <div className="review-content">
                      <label>Contact Details for Pickup</label>
                      <p>{dataForm.contactName}</p>
                      <p>{dataForm.contactPhone}</p>
                    </div>
                    <Divider />
                    <h6 className="sub-title">Drop-off Details</h6>
                    <div className="review-content">
                      <label>Contact Details for Drop-off</label>
                      <p>{dataForm.deliveryInstruction || 'N/A'}</p>
                    </div>
                    <div className="review-content">
                      <label>Contact Details for Pickup</label>
                      <p>{dataForm.deliveryContactName}</p>
                      <p>{dataForm.deliveryContactPhone}</p>
                    </div>
                  </div>
                </>
              )}
              {current === 5 && (
                <>
                  <div className="header-step">
                    <div className="title">
                      <h5>PAYMENT</h5>
                    </div>
                    <div className="content">
                      <p>Use credit or debit card to pay for your job posting</p>
                    </div>
                    <div className="cost">
                      <b>Total Cost</b>
                      <div className="price-wrapper">
                        <div className="price">
                          <h4>$10.00</h4>
                        </div>
                        <Button size="large" type="link">
                          View Cost Breakdown
                          <ReactSVG className="icon" src="/icons/list_alt.svg" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  <Divider />
                  <Row gutter={[20, 24]}>
                    <Col span={12}>
                      <div className="ant-form-item cards">
                        <label className="ant-form-item-no-colon">Payment Method</label>
                        <div className="card">
                          <h5 className="title-card">New Credit/Debit Card</h5>
                          <div className="input">
                            <label className="ant-form-item-no-colon">Payment Method</label>
                            <Input value="4111 1111 1111 1111" disabled suffix={<img src="/Mastercard.png" />} />
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col span={12}></Col>
                  </Row>
                </>
              )}
            </div>
          </Form>
          <div className="FormFooterNewJobOrder">
            <div className="left">
              {current > 0 && (
                <Button size="large" onClick={clickBack}>
                  Back
                </Button>
              )}
            </div>
            <div className="right">
              <Button size="large" className="ButtonLink" onClick={saveAsDraft}>
                Save As Draft
                <ReactSVG className="icon" src="/icons/save.svg" />
              </Button>
              <Button size="large" className="CreateButton ButtonPrimary" type="primary" onClick={clickConfirm}>
                {current === 4 ? 'Confirm to Create' : 'Continue'}
                <ReactSVG className="icon" src="/icons/done.svg" />
              </Button>
            </div>
          </div>
          <Modal open={previewOpen} title={previewTitle} footer={null} onCancel={() => setPreviewOpen(false)}>
            <img alt="preview" style={{ width: '100%' }} src={previewImage} />
          </Modal>
        </div>
      </div>
    </Spin>
  );
};

export default CreateJobOrderPage;
